import { useRef, useEffect } from 'react'
import { AuthCard } from '../components'
import { useLogin } from '../hooks'
import { authApi } from '../api/authApi'
import type { LoginCredentials } from '../types'
import { toast } from 'react-hot-toast'

const SignInPage = () => {
  const loginMutation = useLogin()

  const handleSubmit = (credentials: LoginCredentials) => {
    loginMutation.mutate(credentials)
  }

  // Show toast on error
  useEffect(() => {
    if (loginMutation.error?.message) {
      toast.error(loginMutation.error.message)
    }
  }, [loginMutation.error])

  const gisInitializedRef = useRef(false)

  const handleGoogleAuth = async () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    console.groupCollapsed('[GIS][SignIn] handleGoogleAuth')
    console.log('origin:', window.location.origin)
    console.log('clientId present:', !!clientId)
    console.log('script present:', typeof window.google !== 'undefined')
    console.log('initializedRef:', gisInitializedRef.current)
    if (!clientId) {
      console.error('[GIS][SignIn] Missing VITE_GOOGLE_CLIENT_ID')
      console.groupEnd()
      return
    }
    // Use Google One Tap to get ID token
    const googleId = window.google?.accounts.id
    if (!googleId) {
      console.error('[GIS][SignIn] window.google.accounts.id not available')
      console.groupEnd()
      return
    }

    try { 
      console.log('[GIS][SignIn] cancelling any in-flight session')
      googleId.cancel() 
    } catch (e) { 
      console.warn('[GIS][SignIn] cancel threw (ok to ignore):', e)
    }

    if (!gisInitializedRef.current) {
      console.log('[GIS][SignIn] initializing GIS...')
      googleId.initialize({
        client_id: clientId,
        callback: async (response: { credential?: string }) => {
          try {
            console.log('[GIS][SignIn] callback fired. Has credential:', !!response?.credential)
            const idToken = response.credential
            if (!idToken) return
            console.time('[GIS][SignIn] authApi.googleAuth')
            const user = await authApi.googleAuth(idToken)
            console.timeEnd('[GIS][SignIn] authApi.googleAuth')
            const next = user.role === 'PENDING' ? '/onboarding/role' : '/'
            window.location.href = next
          } catch (e) {
            console.error('[GIS][SignIn] Google auth failed', e)
            toast.error('Google authentication failed. Please try again or use email signin.')
          }
        },
        auto_select: false,
        use_fedcm_for_prompt: true,
      })
      gisInitializedRef.current = true
      console.log('[GIS][SignIn] initialized set -> true')
    }
    // In dev, force OAuth code popup flow to avoid FedCM/ITP/local restrictions
    if (import.meta.env.DEV) {
      try {
        const codeClient = window.google?.accounts.oauth2.initCodeClient({
          client_id: clientId,
          scope: 'email profile openid',
          ux_mode: 'popup',
          redirect_uri: 'postmessage',
          callback: async (resp: { code?: string; error?: string }) => {
            if (resp.error) {
              console.error('[GIS][SignIn][DEV] Code client error:', resp.error)
              toast.error(`Google Sign-In Error: ${resp.error}`)
              return
            }
            if (resp.code) {
              try {
                await authApi.googleAuthWithCode(resp.code)
                window.location.href = '/'
              } catch (err) {
                console.error('[GIS][SignIn][DEV] Backend code exchange failed:', err)
                if (err instanceof Error) {
                  toast.error(err.message)
                } else {
                  toast.error('Google Sign-In Failed')
                }
              }
            }
          }
        })
        codeClient?.requestCode()
        console.groupEnd()
        return
      } catch (err) {
        console.warn('[GIS][SignIn][DEV] Code flow init failed, falling back to prompt:', err)
      }
    }

    // Prefer popup mode to avoid 3rd-party cookie/ITP issues
    try {
      console.log('[GIS][SignIn] prompting...')
      googleId.prompt((notification: { getDismissedReason?: () => string; getNotDisplayedReason?: () => string }) => {
        try {
          const reason = notification?.getDismissedReason?.() || notification?.getNotDisplayedReason?.()
          if (reason) console.warn('[GIS][SignIn] prompt dismissed/not displayed:', reason)
        } catch (err) {
          console.warn('[GIS][SignIn] prompt reason retrieval failed:', err)
        }
      })
    } catch (e) {
      console.error('[GIS][SignIn] Google prompt error, falling back to OAuth code flow', e)
      // Fallback: OAuth code popup
      try {
        const client = window.google?.accounts.oauth2.initCodeClient({
          client_id: clientId,
          scope: 'email profile openid',
          ux_mode: 'popup',
          redirect_uri: 'postmessage',
          callback: async (resp: { code?: string; error?: string }) => {
            if (resp.error) {
              console.error('[GIS][SignIn] Code client error:', resp.error)
              return
            }
            if (resp.code) {
              try {
                const user = await authApi.googleAuthWithCode(resp.code)
                const next = user.role === 'PENDING' ? '/onboarding/role' : '/'
                window.location.href = next
              } catch (err) {
                console.error('[GIS][SignIn] Backend code exchange failed:', err)
                if (err instanceof Error) {
                  toast.error(err.message)
                } else {
                  toast.error('Google Sign-In Failed')
                }
              }
            }
          }
        })
        client?.requestCode()
      } catch (err) {
        console.error('[GIS][SignIn] Code flow init failed:', err)
        toast.error('Failed to show Google sign-in. Please try refreshing the page.')
      }
    }
    console.groupEnd()
  }

  return (
    <AuthCard
      type="signin"
      title="Welcome back"
      subtitle="Sign in to your account to continue"
      onSubmit={handleSubmit}
      onGoogleAuth={handleGoogleAuth}
      isLoading={loginMutation.isPending}
      error={loginMutation.error?.message}
    />
  )
}

export default SignInPage